import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const AccountSettingsPage = () => {
    const navigate = useNavigate();

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "none",
                fontFamily: "Plus Jakarta Sans, sans-serif",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}
        >
            {/* Header */}
            <div style={{ width: "100%", maxWidth: "890px", marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <button
                        onClick={() => navigate('/faqs')}
                        style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "50%",
                            background: "#FFFFFF",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backdropFilter: "blur(10px)"

                        }}
                    >
                        <ArrowLeft size={20} color="#171717" />
                    </button>
                    <h1
                        className='font-clash-display'
                        style={{
                            fontWeight: "600",
                            fontSize: "24px",
                            lineHeight: "32px",
                            color: "#374151",
                            margin: 0
                        }}
                    >
                        Managing Account and Privacy Settings
                    </h1>
                </div>
            </div>

            {/* Content Box */}
            <div
                style={{
                    width: "100%",
                    maxWidth: "890px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "16px",
                    border: "1px solid #E5E7EB",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                    padding: "32px"
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                    {[
                        {
                            title: "Step 1: Access Your Account Settings",
                            points: [
                                "Navigate to the Settings menu on the left sidebar.",
                                "Click on the Account tab to view your profile details."
                            ]
                        },
                        {
                            title: "Step 2: Update Your Profile",
                            points: [
                                "Fill in your personal details:",
                                "First Name",
                                "Last Name",
                                "Email Address",
                                "Phone Number",
                                "To update your profile picture, click on the Upload an image section and select a PNG or JPEG file from your device."
                            ]
                        },
                        {
                            title: "Step 3: Edit Competencies",
                            points: [
                                <>
                                    Choose the competencies you want to add or update from the provided list. Click{' '}
                                    <span style={{ color: "#0891B2", fontWeight: "500", cursor: "pointer" }}>
                                        Save
                                    </span>{" "}
                                    to apply your updates.
                                </>
                            ]
                        },
                        {
                            title: "Step 4: Request Data Deletion",
                            custom: (
                                <>
                                    <OrderedList
                                        items={[
                                            "Click on the Delete My Data section.",
                                            <>
                                                Submit a Deletion Request:
                                                <ul style={{ marginLeft: "16px", marginTop: "8px" }}>
                                                    <ListItem text="Click on the Delete My Data link." />
                                                    <ListItem text="A confirmation pop-up will appear asking you to confirm your decision." />
                                                </ul>
                                            </>,
                                            <>
                                                Confirm Your Action:
                                                <ul style={{ marginLeft: "16px", marginTop: "8px" }}>
                                                    <ListItem text="If you're sure you want to delete your data, click Yes, delete my data." />
                                                    <ListItem text="You will receive a notification once your request is processed." />
                                                </ul>
                                            </>
                                        ]}
                                    />
                                </>
                            )
                        },
                        {
                            title: "Step 5: Save Your Changes",
                            points: [
                                "After updating your information, click the Save Details button at the bottom of the page to ensure all changes are saved."
                            ]
                        }
                    ].map((step, idx) => (
                        <div
                            key={idx}
                            style={{
                                borderBottom: idx < 4 ? "1px solid #D1D5DB" : "none",
                                paddingBottom: "16px"
                            }}
                        >
                            <h2
                                style={{
                                    fontWeight: "700",
                                    fontSize: "18px",
                                    lineHeight: "28px",
                                    color: "#111827",
                                    marginBottom: "16px"
                                }}
                            >
                                {step.title}
                            </h2>
                            {step.custom ? (
                                step.custom
                            ) : (
                                <ul style={{ listStyle: "none", padding: 0 }}>
                                    {step.points.map((point, i) => (
                                        <ListItem key={i} text={point} indent={step.title.includes("Update") && i > 0 && i < 5} />
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const ListItem = ({ text, indent = false }) => (
    <li
        style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            fontSize: "14px",
            lineHeight: "20px",
            color: "#6B7280",
            marginBottom: "8px",
            marginLeft: indent ? "24px" : "0"
        }}
    >
        <span
            className='font-plus-jakarta-sans'
            style={{
                width: "4px",
                height: "4px",
                backgroundColor: "#9CA3AF",
                borderRadius: "50%",
                marginTop: "8px",
                flexShrink: 0
            }}
        />
        <span>{text}</span>
    </li>
);

const OrderedList = ({ items }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {items.map((item, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span
                    style={{
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6B7280",
                        marginTop: "2px",
                        flexShrink: 0
                    }}
                >
                    {idx + 1}
                </span>
                <div style={{ fontSize: "14px", lineHeight: "20px", color: "#6B7280" }}>{item}</div>
            </div>
        ))}
    </div>
);

export default AccountSettingsPage;
